import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { supabase } from '../../lib/supabase';
import { moveToTrash } from '../../lib/trash';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassFill } from '../../v10/ui';
import { RichTextEditor } from './RichTextEditor';

// ---------------------------------------------------------------------------
// NEMOS Notes — native replica of src/app/nemos/notes/page.tsx.
// Same `notes` / `note_folders` tables, same folder → note tree, same soft
// delete into `trash` via the shared mobile port of src/lib/trash.ts
// (moveToTrash — same table/columns web uses, fail-closed on error).
//
// Rich text: web notes are edited with a tiptap rich-text editor
// (src/components/notes/RichTextEditor.tsx) and `notes.content` stores
// tiptap-generated HTML. Mobile now edits the SAME `content` column as real
// HTML via ./RichTextEditor.tsx (a WebView-hosted contentEditable document
// with a matching toolbar), so notes round-trip with formatting intact
// between web and mobile. If the WebView itself fails to load (e.g. no
// WebView engine available on the device), editing falls back to a plain
// multiline TextInput over the raw HTML string — an honest, visible fallback
// rather than a silent data loss; the column and its contents are never
// touched differently by the fallback path.
// ---------------------------------------------------------------------------

interface NoteFolder {
  id: string;
  name: string;
  createdAt: string;
}

interface Note {
  id: string;
  folderId: string;
  title: string;
  content: string;
  updatedAt: string;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function debounce<F extends (...args: any[]) => any>(func: F, waitFor: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<F>): void => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };
}

export function NemosNotes({ userId }: { userId: string }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  const [folders, setFolders] = useState<NoteFolder[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  const [searchTerm, setSearchTerm] = useState('');

  const [itemToDelete, setItemToDelete] = useState<{ type: 'folder' | 'note'; id: string; name: string } | null>(null);

  // WebView rich-text editor error fallback — plain-text-over-HTML editing
  // only kicks in if the WebView itself fails (see RichTextEditor's onError).
  const [editorFailed, setEditorFailed] = useState<Record<string, boolean>>({});

  const fetchFolders = useCallback(async () => {
    const { data, error } = await supabase
      .from('note_folders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (!error) {
      setFolders((data || []).map((row: any) => ({ id: row.id, name: row.name, createdAt: row.created_at })));
    }
  }, [userId]);

  const fetchNotes = useCallback(async () => {
    const { data, error } = await supabase.from('notes').select('*').eq('user_id', userId);
    if (!error) {
      setNotes(
        (data || []).map((row: any) => ({
          id: row.id,
          folderId: row.folder_id,
          title: row.title,
          content: row.content,
          updatedAt: row.updated_at,
        }))
      );
    }
    setIsLoading(false);
  }, [userId]);

  useEffect(() => {
    setIsLoading(true);
    fetchFolders();
    fetchNotes();

    const foldersChannel = supabase
      .channel('nemos_note_folders_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'note_folders', filter: `user_id=eq.${userId}` }, () => fetchFolders())
      .subscribe();
    const notesChannel = supabase
      .channel('nemos_notes_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notes', filter: `user_id=eq.${userId}` }, () => fetchNotes())
      .subscribe();

    return () => {
      supabase.removeChannel(foldersChannel);
      supabase.removeChannel(notesChannel);
    };
  }, [userId, fetchFolders, fetchNotes]);

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      setIsAddingFolder(false);
      return;
    }
    const { data, error } = await supabase
      .from('note_folders')
      .insert({ user_id: userId, name: newFolderName.trim(), created_at: new Date().toISOString() })
      .select()
      .single();
    if (error) {
      Alert.alert('Error creating folder', error.message);
    } else if (data) {
      setFolders((prev) => [{ id: data.id, name: data.name, createdAt: data.created_at }, ...prev]);
      setNewFolderName('');
      setIsAddingFolder(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedFolderId) {
      Alert.alert('Cannot add note', 'Please select a folder first.');
      return;
    }
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('notes')
      .insert({ user_id: userId, folder_id: selectedFolderId, title: 'New Note', content: '', created_at: now, updated_at: now })
      .select()
      .single();
    if (error) {
      Alert.alert('Error creating note', error.message);
    } else if (data) {
      setSelectedNoteId(data.id);
    }
  };

  const confirmDeleteItem = (type: 'folder' | 'note', id: string, name: string) => setItemToDelete({ type, id, name });

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'folder') {
        const notesInFolder = notes.filter((n) => n.folderId === itemToDelete.id);
        const { data: folderData } = await supabase.from('note_folders').select('*').eq('id', itemToDelete.id).single();
        for (const note of notesInFolder) {
          const { data: noteData } = await supabase.from('notes').select('*').eq('id', note.id).single();
          if (noteData) {
            await moveToTrash(supabase, userId, {
              sourceComponent: 'Notes',
              itemType: 'note',
              originalTable: 'notes',
              originalId: note.id,
              itemTitle: note.title || 'Untitled Note',
              itemData: noteData,
            });
          }
        }
        if (folderData) {
          await moveToTrash(supabase, userId, {
            sourceComponent: 'Notes',
            itemType: 'note_folder',
            originalTable: 'note_folders',
            originalId: itemToDelete.id,
            itemTitle: itemToDelete.name,
            itemData: folderData,
          });
        }
        await Promise.all([
          ...notesInFolder.map((note) => supabase.from('notes').delete().eq('id', note.id)),
          supabase.from('note_folders').delete().eq('id', itemToDelete.id),
        ]);
        if (selectedFolderId === itemToDelete.id) {
          setSelectedFolderId(null);
          setSelectedNoteId(null);
        }
      } else {
        const { data: noteData } = await supabase.from('notes').select('*').eq('id', itemToDelete.id).single();
        if (noteData) {
          await moveToTrash(supabase, userId, {
            sourceComponent: 'Notes',
            itemType: 'note',
            originalTable: 'notes',
            originalId: itemToDelete.id,
            itemTitle: itemToDelete.name || 'Untitled Note',
            itemData: noteData,
          });
        }
        const { error } = await supabase.from('notes').delete().eq('id', itemToDelete.id);
        if (error) throw error;
        if (selectedNoteId === itemToDelete.id) setSelectedNoteId(null);
      }
    } catch (err: any) {
      Alert.alert('Error deleting item', err?.message || 'Failed to move item to trash — delete aborted.');
      setItemToDelete(null);
      return;
    }
    setItemToDelete(null);
  };

  const handleStartEditFolder = (folder: NoteFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  };
  const handleCancelEditFolder = () => {
    setEditingFolderId(null);
    setEditingFolderName('');
  };
  const handleSaveFolderName = async () => {
    if (!editingFolderId || !editingFolderName.trim()) {
      handleCancelEditFolder();
      return;
    }
    const { error } = await supabase.from('note_folders').update({ name: editingFolderName.trim() }).eq('id', editingFolderId);
    if (error) Alert.alert('Error renaming folder', error.message);
    handleCancelEditFolder();
  };

  const filteredFolders = useMemo(() => {
    if (!searchTerm) return folders;
    const lower = searchTerm.toLowerCase();
    const folderIdsWithMatchingNotes = new Set(
      notes
        .filter((n) => n.title.toLowerCase().includes(lower) || (typeof n.content === 'string' && n.content.toLowerCase().includes(lower)))
        .map((n) => n.folderId)
    );
    return folders.filter((f) => f.name.toLowerCase().includes(lower) || folderIdsWithMatchingNotes.has(f.id));
  }, [searchTerm, folders, notes]);

  const notesInSelectedFolder = useMemo(() => {
    if (!selectedFolderId) return [];
    return notes
      .filter((n) => n.folderId === selectedFolderId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [notes, selectedFolderId]);

  const activeNote = useMemo(() => notes.find((n) => n.id === selectedNoteId), [notes, selectedNoteId]);

  const debouncedUpdate = useMemo(
    () =>
      debounce((noteId: string, data: { title?: string; content?: string }) => {
        const updateData: Record<string, string> = { updated_at: new Date().toISOString() };
        if (data.title !== undefined) updateData.title = data.title;
        if (data.content !== undefined) updateData.content = data.content;
        supabase
          .from('notes')
          .update(updateData)
          .eq('id', noteId)
          .then(({ error }) => {
            if (error) console.error('Error updating note:', error);
          });
      }, 1000),
    []
  );

  const handleNoteTitleChange = (title: string) => {
    if (!activeNote) return;
    setNotes((prev) => prev.map((n) => (n.id === activeNote.id ? { ...n, title, updatedAt: new Date().toISOString() } : n)));
    debouncedUpdate(activeNote.id, { title });
  };
  const handleNoteContentChange = (content: string) => {
    if (!activeNote) return;
    setNotes((prev) => prev.map((n) => (n.id === activeNote.id ? { ...n, content, updatedAt: new Date().toISOString() } : n)));
    debouncedUpdate(activeNote.id, { content });
  };

  return (
    <View style={styles.root}>
      {!activeNote ? (
        <View style={styles.listPane}>
          <View style={styles.listHeaderRow}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Pressable style={styles.iconBtn} onPress={handleAddNote} disabled={!selectedFolderId}>
              <Text style={[styles.iconBtnText, !selectedFolderId && styles.iconBtnTextDisabled]}>+ Note</Text>
            </Pressable>
            <Pressable style={styles.iconBtn} onPress={() => setIsAddingFolder(true)}>
              <Text style={styles.iconBtnText}>+ Folder</Text>
            </Pressable>
          </View>

          <TextInput
            style={styles.searchInput}
            placeholder="Search notes..."
            placeholderTextColor={t.ivorySoft}
            value={searchTerm}
            onChangeText={setSearchTerm}
          />

          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {isLoading && <ActivityIndicator color={t.goldBright} style={{ marginTop: 16 }} />}

            {isAddingFolder && (
              <View style={styles.addFolderRow}>
                <TextInput
                  autoFocus
                  style={[styles.searchInput, { flex: 1 }]}
                  placeholder="Folder name"
                  placeholderTextColor={t.ivorySoft}
                  value={newFolderName}
                  onChangeText={setNewFolderName}
                  onSubmitEditing={handleAddFolder}
                />
                <Pressable style={styles.saveBtn} onPress={handleAddFolder}>
                  <Text style={styles.saveBtnText}>Save</Text>
                </Pressable>
              </View>
            )}

            {filteredFolders.map((folder) => (
              <View key={folder.id}>
                <Pressable
                  style={[styles.folderRow, selectedFolderId === folder.id && styles.folderRowActive]}
                  onPress={() => setSelectedFolderId(selectedFolderId === folder.id ? null : folder.id)}
                  onLongPress={() => handleStartEditFolder(folder)}
                >
                  <Text style={styles.folderIcon}>📁</Text>
                  {editingFolderId === folder.id ? (
                    <TextInput
                      autoFocus
                      style={styles.folderEditInput}
                      value={editingFolderName}
                      onChangeText={setEditingFolderName}
                      onBlur={handleSaveFolderName}
                      onSubmitEditing={handleSaveFolderName}
                    />
                  ) : (
                    <Text style={styles.folderName} numberOfLines={1}>{folder.name}</Text>
                  )}
                  <Pressable style={styles.deleteBtn} onPress={() => confirmDeleteItem('folder', folder.id, folder.name)}>
                    <Text style={styles.deleteBtnText}>✕</Text>
                  </Pressable>
                </Pressable>
                {selectedFolderId === folder.id && (
                  <View style={styles.notesSubList}>
                    {notesInSelectedFolder.length === 0 && (
                      <Text style={styles.emptyNotesText}>No notes here.</Text>
                    )}
                    {notesInSelectedFolder.map((note) => (
                      <Pressable key={note.id} style={styles.noteRow} onPress={() => setSelectedNoteId(note.id)}>
                        <Text style={styles.noteIcon}>📝</Text>
                        <Text style={styles.noteTitle} numberOfLines={1}>{note.title || 'Untitled'}</Text>
                        <Pressable style={styles.deleteBtn} onPress={() => confirmDeleteItem('note', note.id, note.title)}>
                          <Text style={styles.deleteBtnText}>✕</Text>
                        </Pressable>
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            ))}

            {!isLoading && filteredFolders.length === 0 && (
              <Text style={styles.emptyNotesText}>No folders yet. Create one to get started.</Text>
            )}
          </ScrollView>
        </View>
      ) : (
        <View style={styles.editorPane}>
          <Pressable style={styles.backRow} onPress={() => setSelectedNoteId(null)}>
            <Text style={styles.backText}>‹ Back to Notes</Text>
          </Pressable>
          <TextInput
            style={styles.titleInput}
            value={activeNote.title}
            onChangeText={handleNoteTitleChange}
            placeholder="Note Title"
            placeholderTextColor={t.ivorySoft}
          />
          <Text style={styles.updatedText}>Last updated: {formatRelative(activeNote.updatedAt)}</Text>
          {editorFailed[activeNote.id] ? (
            <TextInput
              style={styles.contentInput}
              value={activeNote.content}
              onChangeText={handleNoteContentChange}
              placeholder="Start writing..."
              placeholderTextColor={t.ivorySoft}
              multiline
              textAlignVertical="top"
            />
          ) : (
            <RichTextEditor
              key={activeNote.id}
              value={activeNote.content}
              onChange={handleNoteContentChange}
              onError={() => setEditorFailed((prev) => ({ ...prev, [activeNote.id]: true }))}
            />
          )}
        </View>
      )}

      <Modal visible={!!itemToDelete} transparent animationType="fade" onRequestClose={() => setItemToDelete(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Are you sure?</Text>
            <Text style={styles.modalBody}>
              {itemToDelete?.type === 'folder'
                ? `This will permanently delete the folder "${itemToDelete?.name}" and ALL notes inside it.`
                : `This will permanently delete the note "${itemToDelete?.name}".`}{' '}
              This action cannot be undone.
            </Text>
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setItemToDelete(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnDanger]} onPress={handleDeleteItem}>
                <Text style={[styles.modalBtnText, styles.modalBtnDangerText]}>Delete</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu, fonts, radii }) =>
  StyleSheet.create({
  root: { flex: 1 },
  listPane: { flex: 1, gap: 8 },
  editorPane: { flex: 1, gap: 8 },
  listHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionTitle: { flex: 1, fontSize: 18, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  iconBtn: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  iconBtnText: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  iconBtnTextDisabled: { color: t.ivorySoft },
  searchInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: t.ivory,
    borderWidth: 1, borderColor: t.navyLine,
  },
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 24, gap: 2 },
  addFolderRow: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  saveBtn: { backgroundColor: t.gold, borderRadius: radii.tile, paddingHorizontal: 14, justifyContent: 'center', borderWidth: 1, borderColor: t.navyLine },
  saveBtnText: { fontWeight: '900', color: t.onGold },
  folderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderRadius: radii.tile,
  },
  folderRowActive: { backgroundColor: t.goldGlow, borderLeftWidth: 2, borderLeftColor: t.gold },
  folderIcon: { fontSize: 16 },
  folderName: { flex: 1, fontSize: 15, fontWeight: '700', color: t.ivory },
  folderEditInput: {
    flex: 1,
    fontSize: 15,
    color: t.ivory,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1, borderColor: t.navyLine,
  },
  deleteBtn: { paddingHorizontal: 8, paddingVertical: 4 },
  deleteBtnText: { color: c.danger, fontSize: 13, fontWeight: '800' },
  notesSubList: { paddingLeft: 22, borderLeftWidth: 2, borderLeftColor: t.navyLine, marginLeft: 14, gap: 2 },
  emptyNotesText: { fontSize: 12, color: t.ivorySoft, paddingVertical: 8, paddingHorizontal: 4 },
  noteRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6, paddingHorizontal: 6 },
  noteIcon: { fontSize: 13 },
  noteTitle: { flex: 1, fontSize: 13, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase' },

  backRow: { paddingVertical: 4 },
  backText: { fontSize: 13, fontWeight: '800', color: t.goldBright },
  titleInput: {
    fontSize: 20,
    fontWeight: '900',
    color: t.ivory,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1, borderColor: t.navyLine,
  },
  updatedText: { fontSize: 11, color: t.ivorySoft },
  contentInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    padding: 12,
    fontSize: 14,
    color: t.ivory,
    minHeight: 260,
    borderWidth: 1, borderColor: t.navyLine,
  },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modalCard: { backgroundColor: 'transparent', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, borderRadius: radii.panel, padding: 18, gap: 12, width: '100%', maxWidth: 380, boxShadow: `0 10px 30px ${t.glowSoft}` as any },
  modalTitle: { fontSize: 16, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  modalBody: { fontSize: 13, color: t.ivorySoft, lineHeight: 18 },
  modalButtons: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end' },
  modalBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.tile, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  modalBtnText: { fontWeight: '800', color: t.ivory },
  modalBtnDanger: { backgroundColor: c.danger },
  modalBtnDangerText: { color: c.white },
}),
);
